// Pinned apps (note §9): link-outs from the dock config on `/v1/me`. They live in the sidebar's
// APPS caption (design spec 2026-10-19 §4; Home's sidebar from Phase 4, every company sidebar
// until then), past six the rest fold into "More". Phones keep one overflow menu in the top bar.
// Embedded apps wait for the app model (after M4).
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  NavList,
  NavRow,
  SidebarCaption,
  TopbarIconButton,
} from "@companyos/ui";
import {
  AppWindowIcon,
  ExternalLinkIcon,
  FolderIcon,
  LayoutGridIcon,
  type LucideIcon,
  MessageSquareIcon,
} from "lucide-react";
import type { DockItem } from "../api/types";

const ICONS: Record<string, LucideIcon> = { folder: FolderIcon, chat: MessageSquareIcon };
export const MAX_APP_ITEMS = 6;

const linksOf = (items: readonly DockItem[]) =>
  items.filter((i) => i.kind === "link_out" && i.href);

export function SidebarApps({ items }: { items: readonly DockItem[] }) {
  const links = linksOf(items);
  if (links.length === 0) return null;
  const shown = links.slice(0, MAX_APP_ITEMS);
  const more = links.slice(MAX_APP_ITEMS);
  return (
    <section data-slot="sidebar-apps" aria-label="Pinned apps">
      <SidebarCaption label="Apps" />
      <NavList>
        {shown.map((item) => (
          <NavRow
            key={item.id}
            icon={ICONS[item.icon ?? ""] ?? AppWindowIcon}
            label={item.label}
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${item.label} (opens in a new tab)`}
            trailing={<ExternalLinkIcon aria-hidden="true" className="size-3.5 text-faint" />}
          />
        ))}
      </NavList>
      {more.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={<Button variant="ghost" size="sm" className="mx-2 mt-px text-secondary" />}
          >
            More
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {more.map((item) => (
              <DropdownMenuItem
                key={item.id}
                onClick={() => window.open(item.href, "_blank", "noopener")}
              >
                {item.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </section>
  );
}

/** Phones: the pinned apps in one overflow menu next to the actions. */
export function MobileAppsMenu({ items }: { items: readonly DockItem[] }) {
  const links = linksOf(items);
  if (links.length === 0) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <TopbarIconButton
            className="md:hidden"
            aria-label="Pinned apps"
            data-slot="mobile-apps"
          />
        }
      >
        <LayoutGridIcon aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {links.map((item) => {
          const Icon = ICONS[item.icon ?? ""] ?? AppWindowIcon;
          return (
            <DropdownMenuItem
              key={item.id}
              onClick={() => window.open(item.href, "_blank", "noopener")}
            >
              <Icon aria-hidden="true" />
              {item.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
